import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Heart, Target, Eye, ShieldCheck, Zap, Users } from "lucide-react";

export default function About() {
    const values = [
        {
            icon: <Target className="text-accent" size={32} />,
            title: "Clarity Over Confusion",
            description: "We believe that most friction in software development comes from ambiguous requirements. We strive to make communication crystal clear."
        },
        {
            icon: <Heart className="text-accent" size={32} />,
            title: "Human-Centric Enforcement",
            description: "Ianos isn't a gatekeeper; it's a helpful guide. We prioritize supportive messages that empower teams rather than policing them."
        },
        {
            icon: <Zap className="text-accent" size={32} />,
            title: "Efficiency Through Quality",
            description: "By ensuring work is 'ready' before it starts, we dramatically reduce waste, back-and-forth, and developer burnout."
        },
        {
            icon: <Users className="text-accent" size={32} />,
            title: "Trust by Default",
            description: "We build tools that foster trust between developers and stakeholders by setting clear expectations and meeting them consistently."
        }
    ];

    return (
        <main className="min-h-screen bg-white">
            <Navbar />

            {/* Hero Section */}
            <section className="pt-32 pb-20 bg-gray-50 border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mb-6">
                            Our mission is to help teams <br className="hidden md:block" />
                            <span className="text-accent">help each other.</span>
                        </h1>
                        <p className="text-xl text-text-muted max-w-3xl mx-auto leading-relaxed">
                            We started Ianos because we saw too much time wasted on incomplete tickets, missing context, and the endless back-and-forth that kills momentum.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Vision & Mission */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="space-y-6"
                        >
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-accent text-sm font-bold uppercase tracking-wider">
                                <Target size={16} />
                                Our Mission
                            </div>
                            <h2 className="text-3xl font-bold text-primary">Eliminating the friction of "incomplete work"</h2>
                            <p className="text-lg text-text-muted leading-relaxed">
                                Our mission is to empower engineering teams to do their best work by ensuring every ticket, bug report, and feature request arrives with the context needed to start immediately. We transform the "Definition of Ready" from a static document into a living, automated assistant.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="space-y-6"
                        >
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-accent text-sm font-bold uppercase tracking-wider">
                                <Eye size={16} />
                                Our Vision
                            </div>
                            <h2 className="text-3xl font-bold text-primary">A world of high-velocity clarity</h2>
                            <p className="text-lg text-text-muted leading-relaxed">
                                We envision a future where the boundary between stakeholders and developers is seamless. A world where developers never have to stop their flow to ask for reproduction steps, and where every project tool helps its users succeed rather than just tracking their tasks.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Product Section */}
            <section className="py-24 bg-primary text-white overflow-hidden relative">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold mb-6">The Product: Gentle Enforcement</h2>
                        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                            Ianos is the validation layer for your favorite project management tools.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl">
                            <ShieldCheck className="text-accent mb-4" size={40} />
                            <h3 className="text-xl font-bold mb-3">Validation Logic</h3>
                            <p className="text-gray-400">Custom rules that check for character counts, required fields, and specific context based on your team's needs.</p>
                        </div>
                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl">
                            <Users className="text-accent mb-4" size={40} />
                            <h3 className="text-xl font-bold mb-3">Instant Feedback</h3>
                            <p className="text-gray-400">Users get immediate guidance right inside Jira or Linear, explaining exactly what is missing and why.</p>
                        </div>
                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl">
                            <Zap className="text-accent mb-4" size={40} />
                            <h3 className="text-xl font-bold mb-3">Zero Overhead</h3>
                            <p className="text-gray-400">Integrates with your existing workflow in minutes. No complex setups or months of training required.</p>
                        </div>
                    </div>
                </div>

                {/* Background blobs */}
                <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1/2 w-96 h-96 bg-accent/20 rounded-full blur-[120px]"></div>
                <div className="absolute bottom-0 right-0 translate-y-1/2 translate-x-1/2 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px]"></div>
            </section>

            {/* Values Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-primary mb-4">What we stand for</h2>
                        <p className="text-lg text-text-muted">The principles that guide every feature we build.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="p-8 rounded-2xl border border-gray-100 hover:shadow-xl hover:border-blue-100 transition-all duration-300"
                            >
                                <div className="mb-6">{value.icon}</div>
                                <h3 className="text-xl font-bold text-primary mb-3">{value.title}</h3>
                                <p className="text-text-muted leading-relaxed">{value.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA section */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h2 className="text-3xl font-bold text-primary mb-8">Ready to see the difference?</h2>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a href="https://app.ianos.io" className="px-8 py-4 bg-accent text-white rounded-xl font-bold hover:bg-accent-dark transition-all transform hover:scale-105 shadow-lg shadow-blue-500/20">
                            Start Your 14-Day Pilot
                        </a>
                        <a href="mailto:nicola.sarli@ianos.io" className="px-8 py-4 bg-white text-primary border border-gray-200 rounded-xl font-bold hover:bg-gray-50 transition-all">
                            Contact Sales
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
